import {
  adjustContentVisibility,
  SectionParagraph as Subtitle,
  SectionTitle as Title,
} from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { Box, Flex, LegacyText, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import React from 'react';
import styled from 'styled-components';
import { Section } from './StyledElements';
import { homeownersImg, homeownersMobileImg, petImg, rentersImg, termLifeImg } from './assets/bundles';

const imageMap: Record<string, { desktop: string; mobile: string }> = {
  homeowners: {
    desktop: homeownersImg.src,
    mobile: homeownersMobileImg.src,
  },
  renters: {
    desktop: rentersImg.src,
    mobile: rentersImg.src,
  },
  pet: {
    desktop: petImg.src,
    mobile: petImg.src,
  },
  life: {
    desktop: termLifeImg.src,
    mobile: termLifeImg.src,
  },
} as const;

const StyledBundleImage = styled(Image)`
  width: 384px;
  height: 351px;
  background-repeat: no-repeat;
  background-position: center;

  ${MEDIA_QUERIES.mobile`
        width: 250px;
        height: 182px;
        background-size: contain;
    `}
`;

const Product = styled(Flex)`
  flex-direction: column;
  border-radius: 10px;

  &:not(:last-of-type) {
    margin-right: 16px;
  }

  ${MEDIA_QUERIES.mobile`
        &:not(:last-of-type) {
            margin-right: 38px;
        }

        img {
            width: 100%;
        }
    `}
`;

const ProductTitle = styled(LegacyText).attrs({
  fontSize: ['1.6rem', '1.8rem'],
  fontWeight: 700,
  lineHeight: '1.9rem',
  type: 'main',
})`
  text-transform: uppercase;
`;

const StyledCTA = styled(JoinCTA)`
  display: inline-block;
  min-width: 233px;
`;

const defaultBundlesRow = [
  {
    image: 'renters',
    title: 'Car + Renters',
    hidden: false,
  },
  {
    image: 'pet',
    title: 'Car + Pet Health',
    hidden: false,
  },
  {
    image: 'life',
    title: 'Car + Term Life',
    hidden: false,
  },
];

interface BundleRowItem {
  title: string | undefined;
  image: string | undefined;
  hidden: boolean | undefined;
}

interface BundlesData {
  main: { title: string | undefined; image: string | undefined };
  row: BundleRowItem[];
}

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

function getMainBundleImageUrl(imageType: string | undefined, isMobileViewport: boolean): string {
  const key = imageType ?? 'homeowners';
  return imageMap[key][isMobileViewport ? 'mobile' : 'desktop'];
}

export interface BundlesProps {
  title?: string;
  subtitle?: string;
  position?: number;
  bundles?: BundlesData;
  ctaLabel?: string;
  ctaUrl?: string;
}

const Bundles: React.FC<BundlesProps> = ({
  title = 'Save even more with our Car + Home bundle', // Default value for "title
  subtitle = 'We’ll automatically apply your bundle discount to any combination of our products', // Default value for 'subtitle
  bundles,
  ctaLabel,
  ctaUrl,
}) => {
  const hideBundlesRow = bundles?.row.every(b => b.hidden === true) === true;
  const { isMobileViewport } = useViewportSize();

  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'bundles',
    page: 'car',
  });

  return (
    <StyledSection mb={0} pt={[50, 100]} width={1} id="bundles" darkBackground contain="paint" ref={intersectionRef}>
      <Flex gap="15px" flexDirection="column" justifyContent="center">
        <Title tabIndex={0}>{parse(title)}</Title>
        <Box mx="auto" width={['80%', '100%']}>
          <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
        </Box>
      </Flex>
      <Flex flexDirection="column" mt={45} justifyContent="center">
        <StyledBundleImage
          src={getMainBundleImageUrl(bundles?.main.image, isMobileViewport)}
          role="img"
          alt={bundles?.main.title ?? 'car and home'}
          width={384}
          height={351}
          aria-label={`${bundles?.main.title ?? 'car and home'} insurance bundling`}
        />
        <Flex mt={20} justifyContent="center" alignItems="center">
          <StyledCTA
            size="small"
            flavor="primary"
            glowing
            data-source="car"
            product="car"
            url={ctaUrl ?? 'https://www.lemonade.com/you/1?f=1&p=car,home_us_owners'}
          >
            {ctaLabel ?? 'Car + Home'}
          </StyledCTA>
        </Flex>
      </Flex>
      {hideBundlesRow ? null : (
        <Flex
          mt={40}
          width="100%"
          overflowX="auto"
          px={[30, 0]}
          pb={[50, 100, 100, 100]}
          justifyContent={['flex-start', 'center']}
          alignItems="flex-end"
        >
          {(bundles?.row ?? defaultBundlesRow).map(({ title, image, hidden }, ind) =>
            hidden ? null : (
              <Product key={title} width={240} justifyContent="center" alignItems="center">
                <Image
                  src={getMainBundleImageUrl(image ?? defaultBundlesRow[ind].image, isMobileViewport)}
                  alt={title ? title : ''}
                  width={120}
                  height={111}
                />
                <ProductTitle mt={16}>{title ?? defaultBundlesRow[ind].title}</ProductTitle>
              </Product>
            ),
          )}
        </Flex>
      )}
    </StyledSection>
  );
};

export default React.memo(Bundles);
